import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X, Clock, Check, ChevronRight, Scissors, Sparkles, Package, Home, LayoutGrid, Tag, Star, MapPin, PlayCircle } from "lucide-react";
import { rupee } from "../shared";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace("://billing.definesalon.com", "://billing.definesalons.com");
// `w` requests a resized, long-cached variant from /api/public/file (cards 480px, banners 1200px) for fast mobile loads
export const photoUrl = (p, w) => {
  if (!p) return "";
  const u = p.startsWith("http") ? p : `${BACKEND}${p}`;
  return w && u.includes("/api/public/file/") ? `${u}${u.includes("?") ? "&" : "?"}w=${w}` : u;
};

export const SkeletonCard = ({ wide }) => (
  <div className={`snap-start shrink-0 ${wide ? "w-[300px] sm:w-[340px]" : "w-[168px] sm:w-[200px] [.grid>&]:w-auto"} flex flex-col animate-pulse`} data-testid="skeleton-card">
    <div className={`rounded-2xl bg-[#ececef] ${wide ? "aspect-[16/11]" : "aspect-[4/5]"}`} />
    <div className="mt-2 h-4 w-3/4 rounded bg-[#ececef]" /><div className="mt-2 h-3 w-1/3 rounded bg-[#ececef]" />
    <div className="mt-2 h-4 w-1/2 rounded bg-[#ececef]" /><div className="mt-2 h-10 w-full rounded-xl bg-[#ececef]" />
  </div>
);
const mins = (m) => { const h = Math.floor((m || 0) / 60), mm = (m || 0) % 60; return h ? `${h} hr${mm ? ` ${mm} mins` : ""}` : `${mm} mins`; };
const off = (price, actual) => (actual > price && actual > 0 ? Math.round((1 - price / actual) * 100) : 0);
const slug = (t) => (t || "x").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const HScroll = ({ children, testid }) => (
  <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x scroll-px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid={testid}>{children}</div>
);
const SectionTitle = ({ children, onMore, testid }) => (
  <div className="flex items-center justify-between mt-7 mb-3 px-0.5">
    <h2 className="text-[19px] sm:text-[22px] font-bold tracking-tight text-[#1c1c1c]" data-testid={testid}>{children}</h2>
    {onMore && <button type="button" onClick={onMore} className="text-primary p-1" aria-label="See all"><ChevronRight size={20} /></button>}
  </div>
);

/* ---- service card (YesMadam "Trending" style) ---- */
export function QtyControl({ qty, onChange, testid, light }) {
  return (
    <div className={`h-10 w-full rounded-xl border-2 flex items-center justify-between px-1 ${light ? "border-white/70 text-white" : "border-primary bg-primary text-white"}`} data-testid={`${testid}-qty`}>
      <button type="button" aria-label="Decrease" onClick={(e) => { e.stopPropagation(); onChange(qty - 1); }} className="h-8 w-9 grid place-items-center text-lg font-bold" data-testid={`${testid}-minus`}>−</button>
      <span className="font-bold text-[15px]" data-testid={`${testid}-count`}>{qty}</span>
      <button type="button" aria-label="Increase" disabled={qty >= 4} onClick={(e) => { e.stopPropagation(); onChange(qty + 1); }} className="h-8 w-9 grid place-items-center text-lg font-bold disabled:opacity-40" data-testid={`${testid}-plus`}>+</button>
    </div>
  );
}

/* ---- optional per-service video (opens in an overlay; booking progress is untouched) ---- */
const embedUrl = (u) => {
  const yt = u.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return { type: "iframe", src: `https://www.youtube.com/embed/${yt[1]}?autoplay=1&playsinline=1` };
  const ig = u.match(/instagram\.com\/(?:reel|p|reels)\/([\w-]+)/);
  if (ig) return { type: "iframe", src: `https://www.instagram.com/reel/${ig[1]}/embed` };
  const vm = u.match(/vimeo\.com\/(\d+)/);
  if (vm) return { type: "iframe", src: `https://player.vimeo.com/video/${vm[1]}?autoplay=1` };
  if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(u)) return { type: "video", src: u };
  return { type: "iframe", src: u };
};

export function VideoModal({ url, title, onClose }) {
  const e = embedUrl(url);
  return (
    <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-3" onClick={onClose} data-testid="service-video-modal">
      <div className="relative w-full max-w-md bg-black rounded-2xl overflow-hidden" onClick={(ev) => ev.stopPropagation()}>
        <div className="flex items-center justify-between px-3 py-2 text-white">
          <p className="text-sm font-semibold truncate pr-2" data-testid="service-video-title">{title}</p>
          <button type="button" onClick={onClose} aria-label="Close video" className="h-8 w-8 grid place-items-center rounded-full bg-white/15" data-testid="service-video-close"><X size={16} /></button>
        </div>
        <div className="aspect-[9/16] max-h-[75vh] w-full bg-black">
          {e.type === "video"
            ? <video src={e.src} controls autoPlay playsInline className="h-full w-full object-contain" data-testid="service-video-player" />
            : <iframe src={e.src} title={title} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen className="h-full w-full border-0" data-testid="service-video-player" />}
        </div>
        <a href={url} target="_blank" rel="noreferrer" className="block text-center text-[11px] text-white/60 py-1.5 underline">Open video in new tab</a>
      </div>
    </div>
  );
}

export function ServiceCard({ s, inCart, onToggle, wide, qty = 0, onQty }) {
  const pct = off(s.price, s.actual_price);
  const [video, setVideo] = useState(false);
  const hasVideo = !!(s.video_url || "").trim();
  return (
    <div className={`snap-start shrink-0 ${wide ? "w-[300px] sm:w-[340px]" : "w-[168px] sm:w-[200px] [.grid>&]:w-auto"} flex flex-col`} data-testid={`home-service-${s.id}`}>
      <div className={`relative rounded-2xl overflow-hidden bg-[#f1f1f3] ${wide ? "aspect-[16/11]" : "aspect-[4/5]"}`}>
        {s.image ? <img src={photoUrl(s.image, 480)} alt={s.name} className="h-full w-full object-cover" loading="lazy" decoding="async" />
          : <div className="h-full w-full grid place-items-center text-primary/60"><Scissors size={30} /></div>}
        {s.category && <span className="absolute top-0 left-0 rounded-br-xl bg-black/55 text-white text-[11px] font-medium px-2.5 py-1 max-w-[85%] truncate">{s.category}</span>}
        {pct > 0 && <span className="absolute top-2 right-2 rounded-full bg-green-600 text-white text-[10px] font-bold px-2 py-0.5">{pct}% OFF</span>}
      </div>
      <p className="mt-2 text-[14px] sm:text-[15px] font-semibold leading-snug line-clamp-2 min-h-[38px]">{s.name}</p>
      <p className="mt-1 text-[12px] text-muted-foreground flex items-center gap-1"><Clock size={12} /> {mins(s.duration)}</p>
      <div className="mt-1 flex items-baseline gap-1.5 flex-wrap" data-testid={`home-service-price-${s.id}`}>
        <span className="text-[15px] font-bold">{rupee(s.price)}</span>
        {pct > 0 && <><span className="text-[12px] text-muted-foreground line-through">{rupee(s.actual_price)}</span><span className="text-[11px] font-semibold text-green-700">{pct}% OFF</span></>}
      </div>
      {inCart && onQty ? <div className="mt-2"><QtyControl qty={qty || 1} onChange={(q) => onQty(s, q)} testid={`home-select-${s.id}`} /></div> : (
        <div className="mt-2 flex gap-1.5">
          {hasVideo && (
            <button type="button" onClick={(e) => { e.stopPropagation(); setVideo(true); }} data-testid={`home-video-${s.id}`}
              className="h-10 w-1/2 px-1 rounded-xl border-2 border-[#1c1c1c]/70 text-[#1c1c1c] bg-white text-[12px] font-semibold inline-flex items-center justify-center gap-1 hover:bg-black/5 transition-colors whitespace-nowrap">
              <PlayCircle size={14} /> See Video
            </button>)}
          <button type="button" onClick={() => onToggle(s)} data-testid={`home-select-${s.id}`}
            className={`h-10 ${hasVideo ? "w-1/2 px-1 text-[12px] whitespace-nowrap" : "w-full text-[14px]"} rounded-xl border-2 font-semibold transition-colors ${inCart ? "bg-primary border-primary text-white" : "border-primary/70 text-primary bg-white hover:bg-primary/5"}`}>
            {inCart ? <span className="inline-flex items-center gap-1"><Check size={15} /> Added</span> : "Add To Cart"}
          </button>
        </div>)}
      {video && <VideoModal url={s.video_url} title={s.name} onClose={() => setVideo(false)} />}
    </div>
  );
}

/* ---- highlight card (image with gradient footer + ADD) ---- */
function HighlightCard({ s, inCart, onToggle, qty = 0, onQty }) {
  const pct = off(s.price, s.actual_price);
  const [video, setVideo] = useState(false);
  const hasVideo = !!(s.video_url || "").trim();
  return (
    <div className="snap-start shrink-0 w-[290px] sm:w-[340px] rounded-2xl overflow-hidden border border-border bg-white shadow-sm" data-testid={`home-highlight-${s.id}`}>
      <div className="relative aspect-[16/10] bg-[#f4efe6]">
        {(s.offer_image || s.image) ? <img src={photoUrl(s.offer_image || s.image, 800)} alt={s.name} className="h-full w-full object-cover" loading="lazy" decoding="async" /> : <div className="h-full w-full grid place-items-center text-primary/60"><Sparkles size={34} /></div>}
        {pct > 0 && <span className="absolute bottom-3 left-3 rounded-full bg-white/95 text-[11px] font-semibold px-2.5 py-1 shadow">Save {rupee((s.actual_price || 0) - (s.price || 0))}</span>}
      </div>
      <div className="p-3.5 bg-gradient-to-r from-[#B71C2B] to-[#e0523f] text-white flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[15px] leading-tight truncate">{s.name}</p>
          <p className="text-[13px] mt-1 flex items-center gap-2"><span className="font-bold">{rupee(s.price)}</span>{pct > 0 && <span className="line-through opacity-75 text-[12px]">{rupee(s.actual_price)}</span>}<span className="opacity-90">| <Clock size={12} className="inline -mt-0.5" /> {mins(s.duration)}</span></p>
        </div>
        {inCart && onQty ? <div className="w-28"><QtyControl qty={qty || 1} onChange={(q) => onQty(s, q)} testid={`home-highlight-add-${s.id}`} light /></div> : (
          <div className="flex gap-1.5 shrink-0">
            {hasVideo && <button type="button" onClick={(e) => { e.stopPropagation(); setVideo(true); }} data-testid={`home-highlight-video-${s.id}`}
              className="h-11 px-3 rounded-xl font-bold text-[13px] bg-white/20 text-white border border-white/60 inline-flex items-center gap-1"><PlayCircle size={15} /> See Video</button>}
            <button type="button" onClick={() => onToggle(s)} data-testid={`home-highlight-add-${s.id}`}
              className={`h-11 px-5 rounded-xl font-bold text-[15px] ${inCart ? "bg-white/25 text-white border border-white/60" : "bg-white text-primary"}`}>{inCart ? "Added" : "ADD"}</button>
          </div>)}
      </div>
      {video && <VideoModal url={s.video_url} title={s.name} onClose={() => setVideo(false)} />}
    </div>
  );
}

/* ---- package card ---- */
function PackageCard({ p, onBook }) {
  const pct = off(p.offer_price, p.actual_price);
  const imgs = (p.services || []).map((s) => s.image).filter(Boolean).slice(0, 4);
  return (
    <div className="snap-start shrink-0 w-[320px] sm:w-[380px] rounded-2xl border border-border bg-white shadow-sm p-4 flex flex-col" data-testid={`home-pkg-${p.id}`}>
      <div className="flex gap-3">
        <div className="h-24 w-24 shrink-0 rounded-xl overflow-hidden grid grid-cols-2 gap-0.5 bg-[#f1f1f3]">
          {p.image ? <img src={photoUrl(p.image, 480)} alt={p.name} loading="lazy" decoding="async" className="col-span-2 h-full w-full object-cover" />
            : imgs.length ? imgs.map((im, i) => <img key={i} src={photoUrl(im, 240)} alt="" loading="lazy" decoding="async" className={`h-full w-full object-cover ${imgs.length === 1 ? "col-span-2" : ""}`} />)
            : <div className="col-span-2 grid place-items-center text-primary/60"><Package size={28} /></div>}
        </div>
        <div className="min-w-0">
          <p className="font-bold text-[17px] leading-tight">{p.name}</p>
          {p.duration > 0 && <p className="text-[13px] text-muted-foreground mt-1 flex items-center gap-1"><Clock size={13} /> {mins(p.duration)}</p>}
        </div>
      </div>
      <ul className="mt-3 space-y-1 text-[13px] text-[#333] list-disc pl-4 min-h-[60px]" data-testid={`home-pkg-combo-${p.id}`}>
        {(p.services || []).slice(0, 4).map((s, i) => <li key={i}><span className="font-medium">{s.category ? `${s.category}: ` : ""}</span>{s.name}</li>)}
        {(p.services || []).length > 4 && <li className="text-muted-foreground">+{p.services.length - 4} more</li>}
      </ul>
      <div className="mt-3 pt-3 border-t border-border flex items-center gap-2">
        <span className="text-[20px] font-bold">{rupee(p.offer_price)}</span>
        {pct > 0 && <><span className="text-[13px] text-muted-foreground line-through">{rupee(p.actual_price)}</span><span className="text-[13px] font-bold text-green-700" data-testid={`home-pkg-save-${p.id}`}>{pct}% OFF</span></>}
        <button type="button" onClick={() => onBook(p)} data-testid={`home-pkg-book-${p.id}`} className="ml-auto h-11 px-5 rounded-xl bg-primary text-white font-bold text-[15px] hover:bg-[#9d1826]">Add to Cart</button>
      </div>
    </div>
  );
}

export default function HomeFeed({ loading, services, categories, banners, offer, homeCards, heroFor, cardGo, pkgs, fpCfg, cart, toggleService, setQty, startPackage, startFreedom, setView, svcQuery, setSvcQuery, gender, setGender }) {
  const [trendCat, setTrendCat] = useState("");
  const forGender = (s, g) => g === "all" || s.gender === "unisex" || !s.gender || s.gender === (g === "women" ? "female" : "male");
  const list = useMemo(() => services.filter((s) => forGender(s, gender)), [services, gender]);
  const cats = useMemo(() => {
    const map = new Map();
    list.forEach((s) => { const c = s.category || "Other"; map.set(c, (map.get(c) || 0) + 1); });
    const ordered = categories.map((c) => c.name).filter((n) => map.has(n));
    return [...ordered, ...[...map.keys()].filter((n) => !ordered.includes(n))].map((name) => ({ name, count: map.get(name), image: (categories.find((c) => c.name === name) || {}).image }));
  }, [list, categories]);
  const offers = list.filter((s) => (s.actual_price || 0) > (s.price || 0));
  const trending = (trendCat ? list.filter((s) => (s.category || "Other") === trendCat) : list).slice(0, 12);
  const pk = pkgs.filter((p) => { const pg = p.gender || "women"; return gender === "men" ? (pg === "men" || pg === "unisex") : (pg === "women" || pg === "unisex" || !p.gender); });
  const freedom = gender === "women" && fpCfg?.enabled && (fpCfg.slabs || []).length > 0;
  const inCart = (s) => !!cart.find((x) => x.id === s.id);
  const qtyOf = (s) => cart.find((x) => x.id === s.id)?.qty || 0;
  const q = svcQuery.trim().toLowerCase();
  const results = q ? list.filter((s) => (s.name || "").toLowerCase().includes(q) || (s.category || "").toLowerCase().includes(q)) : [];
  const hint = list[Math.floor(list.length / 2)]?.name || "Hair Spa";
  const slides = [
    ...banners.map((b, i) => ({ key: `b${i}`, image: b.image, title: b.heading, sub: b.offer_text, price: b.new_price_text, old: b.old_price_text, go: () => setView({ type: "svclist", gender, category: null, offersOnly: true, title: "Special Offers" }) })),
    ...(!banners.length && offer && (offer.title || offer.image) ? [{ key: "o", image: offer.image, title: offer.title, sub: offer.description, price: offer.price_text, go: () => setView({ type: "svclist", gender, category: null, offersOnly: true, title: "Special Offers" }) }] : []),
    ...(homeCards || []).filter((c) => c.show !== false && c.image).map((c) => ({ key: c.id, image: heroFor(c), title: c.title, sub: c.subtitle, go: () => cardGo(c) })),
  ];

  return (
    <div className="pb-36" data-testid="home-feed">
      {/* Women / Men toggle */}
      <div className="flex justify-center mt-3">
        <div className="inline-flex rounded-full bg-white border border-border p-1 shadow-sm" data-testid="home-gender-toggle">
          {[["women", "Salon for Women"], ["men", "Salon for Men"]].map(([g, l]) => (
            <button key={g} type="button" onClick={() => { setGender(g); setTrendCat(""); }} data-testid={`home-gender-${g}`}
              className={`px-5 h-10 rounded-full text-[14px] font-semibold transition-colors ${gender === g ? "bg-primary text-white" : "text-[#444]"}`}>{l}</button>
          ))}
        </div>
      </div>

      {q ? (
        <>
          <SectionTitle testid="home-search-title">Results for “{svcQuery}”</SectionTitle>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="cats-search-results">
            {results.map((s) => <ServiceCard key={s.id} s={s} inCart={inCart(s)} onToggle={toggleService} qty={qtyOf(s)} onQty={setQty} />)}
            {!results.length && <p className="col-span-full text-sm text-muted-foreground py-10 text-center">No services match “{svcQuery}”.</p>}
          </div>
        </>
      ) : (
        <>
          {/* Banners */}
          {slides.length > 0 && (
            <HScroll testid="home-banners">
              {slides.map((b) => (
                <button key={b.key} type="button" onClick={b.go} className="snap-center shrink-0 w-[88%] sm:w-[520px] aspect-[2/1] sm:aspect-[5/2] rounded-2xl overflow-hidden relative text-left shadow-sm mt-4" data-testid={`home-banner-${slug(b.title)}`}>
                  {b.image && <img src={photoUrl(b.image, 1200)} alt={b.title || ""} decoding="async" className="absolute inset-0 h-full w-full object-cover" />}
                  <div className={`absolute inset-0 ${b.image ? "bg-gradient-to-r from-black/70 via-black/30 to-transparent" : "bg-gradient-to-br from-primary to-[#7d1420]"}`} />
                  <div className="absolute inset-0 p-5 flex flex-col justify-center text-white">
                    <p className="text-[22px] sm:text-[28px] font-bold leading-tight max-w-[70%]">{b.title}</p>
                    {b.sub && <p className="text-[13px] mt-1 opacity-90 max-w-[70%] line-clamp-2">{b.sub}</p>}
                    <p className="mt-2 flex items-baseline gap-2">{b.old && <span className="line-through opacity-70 text-sm">{b.old}</span>}{b.price && <span className="font-bold text-lg">{b.price}</span>}</p>
                    <span className="mt-auto text-[10px] opacity-75">*T&C APPLY</span>
                  </div>
                </button>
              ))}
            </HScroll>
          )}

          {/* Categories */}
          <SectionTitle testid="home-cats-title">What are you looking for?</SectionTitle>
          <HScroll testid="cat-grid">
            {cats.map((c) => (
              <button key={c.name} type="button" onClick={() => setView({ type: "svclist", gender, category: c.name, title: c.name })} data-testid={`cat-card-${slug(c.name)}`} className="snap-start shrink-0 w-[96px] sm:w-[120px] text-center">
                <div className="aspect-square rounded-2xl overflow-hidden bg-[#fbeee9] border border-border">
                  {c.image ? <img src={photoUrl(c.image, 480)} alt={c.name} className="h-full w-full object-cover" loading="lazy" /> : <div className="h-full w-full grid place-items-center text-primary"><Scissors size={26} /></div>}
                </div>
                <p className="mt-1.5 text-[12px] sm:text-[13px] font-medium leading-tight line-clamp-2">{c.name}</p>
              </button>
            ))}
            {(pk.length > 0 || freedom) && (
              <button type="button" onClick={() => setView({ type: "packages", gender, title: gender === "men" ? "Men Packages" : "Women Packages" })} data-testid="cat-card-packages" className="snap-start shrink-0 w-[96px] sm:w-[120px] text-center">
                <div className="aspect-square rounded-2xl bg-primary/10 border border-primary/30 grid place-items-center text-primary"><Package size={30} /></div>
                <p className="mt-1.5 text-[12px] sm:text-[13px] font-medium">Packages</p>
              </button>
            )}
          </HScroll>

          {/* Offers */}
          {offers.length > 0 && (
            <>
              <SectionTitle testid="home-offers-title" onMore={() => setView({ type: "svclist", gender, category: null, offersOnly: true, title: "Special Offers" })}>Special Offers</SectionTitle>
              <HScroll testid="home-offers">{offers.slice(0, 10).map((s) => <HighlightCard key={s.id} s={s} inCart={inCart(s)} onToggle={toggleService} qty={qtyOf(s)} onQty={setQty} />)}</HScroll>
            </>
          )}

          {/* Trending */}
          <SectionTitle testid="home-trending-title">Trending at DEFINE</SectionTitle>
          <HScroll testid="home-trend-chips">
            {["", ...cats.map((c) => c.name)].map((c) => (
              <button key={c || "all"} type="button" onClick={() => setTrendCat(c)} data-testid={`trend-chip-${slug(c || "all")}`}
                className={`snap-start shrink-0 h-10 px-4 rounded-full border text-[14px] font-medium ${trendCat === c ? "bg-primary border-primary text-white" : "bg-white border-[#333]/70 text-[#222]"}`}>{c || "All"}</button>
            ))}
          </HScroll>
          <HScroll testid="home-trending">{trending.map((s) => <ServiceCard key={s.id} s={s} inCart={inCart(s)} onToggle={toggleService} qty={qtyOf(s)} onQty={setQty} />)}{!trending.length && (loading ? [0, 1, 2].map((i) => <SkeletonCard key={i} />) : <p className="text-sm text-muted-foreground py-6">No services yet.</p>)}</HScroll>

          {/* Packages */}
          {(pk.length > 0 || freedom) && (
            <>
              <SectionTitle testid="home-pkgs-title" onMore={() => setView({ type: "packages", gender, title: "Packages" })}>Packages & Save Extra</SectionTitle>
              <HScroll testid="home-packages">
                {freedom && (
                  <div className="snap-start shrink-0 w-[320px] sm:w-[380px] rounded-2xl border-2 border-primary bg-white shadow-sm p-4 flex flex-col" data-testid="pkg-freedom-card">
                    <div className="flex items-center gap-2"><span className="h-10 w-10 rounded-xl bg-primary/10 text-primary grid place-items-center"><Sparkles size={20} /></span><p className="font-bold text-[17px]">Freedom Package</p></div>
                    <p className="text-[13px] text-muted-foreground mt-2">Pick any services you love — one fixed price.</p>
                    <ul className="mt-2 space-y-1 text-[13px] list-disc pl-4 flex-1" data-testid="pkg-freedom-slabs">{[...fpCfg.slabs].sort((a, b) => a.count - b.count).map((s) => <li key={s.count} data-testid={`pkg-freedom-slab-${s.count}`}>Any {s.count} services — <b className="text-primary">{rupee(s.price)}</b></li>)}</ul>
                    <button type="button" onClick={startFreedom} className="mt-3 h-11 rounded-xl bg-primary text-white font-bold text-[15px]">Build my package</button>
                  </div>
                )}
                {pk.map((p) => <PackageCard key={p.id} p={p} onBook={startPackage} />)}
              </HScroll>
            </>
          )}

          {/* Per-category rows */}
          {cats.map((c) => {
            const items = list.filter((s) => (s.category || "Other") === c.name);
            if (!items.length) return null;
            return (
              <div key={c.name} data-testid={`home-cat-row-${slug(c.name)}`}>
                <SectionTitle onMore={() => setView({ type: "svclist", gender, category: c.name, title: c.name })}>{c.name}</SectionTitle>
                <HScroll>{items.slice(0, 10).map((s) => <ServiceCard key={s.id} s={s} inCart={inCart(s)} onToggle={toggleService} qty={qtyOf(s)} onQty={setQty} />)}</HScroll>
              </div>
            );
          })}

          <div className="mt-10 -mx-4 px-6 py-10 bg-[#f1f1f4] text-[#b7b7bd]">
            <p className="text-[30px] sm:text-[40px] font-extrabold leading-tight">Hyderabad's Most Loved<br />Salon Experience</p>
            <div className="border-t border-[#d9d9de] my-5" />
            <p className="text-[15px]">Crafted with <span className="text-primary">♥</span> by team <span className="font-semibold text-[#8d8d94]">DEFINE SALONS</span></p>
            <Link to="/rate" className="inline-block mt-3 text-[13px] text-primary" data-testid="home-rate-visit">Been here before? Rate your visit</Link>
          </div>
        </>
      )}
    </div>
  );
}

/* ---- sticky top header (cream) ---- */
export function BookHeader({ branches, svcQuery, setSvcQuery, hint, onHome, onPackages, showSearch }) {
  return (
    <header className="sticky top-0 z-30 bg-[#fbf6ea] border-b border-[#eee5cf]" data-testid="book-header">
      <div className="max-w-6xl mx-auto px-4 pt-3 pb-3">
        <div className="flex items-center gap-2">
          <button type="button" onClick={onHome} className="min-w-0 text-left" data-testid="nav-home">
            <p className="flex items-center gap-1 text-[18px] font-bold leading-tight"><MapPin size={17} className="text-primary shrink-0" /><span className="truncate">DEFINE <span className="text-primary">SALONS</span></span></p>
            <p className="text-[12px] text-[#555] truncate">{branches.length ? `${branches.length} branch${branches.length > 1 ? "es" : ""} · ${branches.map((b) => (b.name || "").split("–").pop().trim()).join(", ")}` : "Premium unisex salon"}</p>
          </button>
          <Link to="/rate" className="ml-auto shrink-0 h-11 px-3.5 rounded-full bg-[#3a3a3a] text-white text-[12px] font-semibold leading-tight flex items-center gap-1.5" data-testid="nav-rate-visit"><Star size={14} className="text-[#f2c14e]" /> Rate &<br />Review</Link>
          <button type="button" onClick={onPackages} className="shrink-0 h-11 px-3.5 rounded-full bg-[#3a3a3a] text-[12px] font-semibold leading-tight text-left" data-testid="nav-packages"><span className="text-white">Buy</span><br /><span className="text-[#f2c14e] font-serif italic text-[14px]">Packages</span></button>
        </div>
        {showSearch && (
          <div className="relative mt-3">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#666]" />
            <input value={svcQuery} onChange={(e) => setSvcQuery(e.target.value)} data-testid="home-search" placeholder={`Search for '${hint}'`}
              className="h-[52px] w-full rounded-2xl bg-white border border-[#e2dccb] pl-12 pr-10 py-3.5 text-[15px] placeholder:text-[#777] focus:outline-none focus:ring-2 focus:ring-primary/30" />
            {svcQuery && <button type="button" onClick={() => setSvcQuery("")} data-testid="home-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666]"><X size={16} /></button>}
          </div>
        )}
      </div>
    </header>
  );
}

/* ---- sticky View Cart bar + bottom nav ---- */
export function BottomBars({ cart, gender, onViewCart, view, setView, onHome, showNav }) {
  const NAV = [
    ["home", "Home", Home, onHome],
    ["cats", "Categories", LayoutGrid, () => setView({ type: "cats", gender, title: gender === "men" ? "MEN" : "WOMEN" })],
    ["offers", "Offers", Tag, () => setView({ type: "svclist", gender, category: null, offersOnly: true, title: "Special Offers" })],
    ["packages", "Packages", Package, () => setView({ type: "packages", gender, title: "Packages" })],
  ];
  const activeKey = view.type === "svclist" && view.offersOnly ? "offers" : view.type;
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 pointer-events-none">
      {cart.length > 0 && (
        <div className="max-w-6xl mx-auto px-3 pb-2 pointer-events-auto" data-testid="home-addservice-bar">
          <button type="button" onClick={onViewCart} data-testid="home-continue" className="w-full rounded-2xl bg-[#1f8a3b] text-white shadow-xl px-4 py-3 flex items-center gap-3 text-left">
            <span className="h-10 w-10 rounded-full bg-white/20 grid place-items-center"><Scissors size={18} /></span>
            <span className="flex-1 min-w-0"><span className="block font-bold text-[15px]">Salon For {gender === "men" ? "Men" : "Women"}</span><span className="block text-[12px] opacity-90" data-testid="home-cart-summary">{cart.reduce((a, x) => a + (x.qty || 1), 0)} service{cart.reduce((a, x) => a + (x.qty || 1), 0) > 1 ? "s" : ""} added · {rupee(cart.reduce((a, x) => a + (x.price || 0) * (x.qty || 1), 0))}</span></span>
            <span className="font-bold text-[16px] inline-flex items-center gap-1">View Cart <ChevronRight size={18} /></span>
          </button>
        </div>
      )}
      {showNav && (
        <nav className="bg-white border-t border-border pointer-events-auto" data-testid="home-bottom-nav">
          <div className="max-w-6xl mx-auto grid grid-cols-4">
            {NAV.map(([k, l, Icon, go]) => (
              <button key={k} type="button" onClick={go} data-testid={`bottom-nav-${k}`} className={`h-14 flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium ${activeKey === k ? "text-primary" : "text-[#555]"}`}><Icon size={20} />{l}</button>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}
